import { WorkbookDomain } from '@mathflat/domain/@entities/(Content)/Workbook/domain.ts'
import type { MouseEvent } from 'react'
import { generatePath } from 'react-router'
import { createSearchParams } from 'react-router-dom'

import { tJosa } from '~/@app/i18n'
import { routeName } from '~/@common/constants'
import { studentWorkbookDetailPathParams } from '~/@common/constants/route'
import type { StudentFeatureKey } from '~/@common/models/(Permission)/FeaturePermission'
import { resolveWorkbookViewRoute } from '~/@common/models/(Permission)/permissionUtils'
import modalService from '~/@common/services/modal.service'
import { commonRepo } from '~/@common/services/repo.service'
import { toastService } from '~/@common/services/toast.service'
import { examLearningContentStatusText, learningContentStatusText } from '~/@common/types'
import Modal from '~/@common/ui/modal/Modal'
import { mixpanelClient } from '~/@common/utils/trackings/mixpanel'
import { openAcademyGroupAccessBlockedModal } from '~/@pages/student/@common/openAcademyGroupAccessBlockedModal'

import type RecentStudy from '../../@common/model/RecentStudy'
import { type RecentStudyTabType, recentStudyTypeText } from '../../@common/types'

/**
 * 학습 카드 클릭 시 이동할 href 를 반환.
 * 학부모 판별: 각 type 의 `.write` restriction 이 토큰에 내려와 있으면 학부모로 간주.
 * 학부모는 /parent/* 라우트 + 학습지/숙제는 무조건 scoring view, 학생은 기존 /student/* 라우트.
 * - WORKSHEET: /student/student-worksheet/:id
 * - WORKBOOK: 일반/시그니처 여부에 따라 detail/viewer 경로
 * - EXAM: /student/student-exam/:id/scoring?title=..&size=..&problemIndex=..&examType=..
 * - 식별자 부재 시 '#' 반환 (클릭 시 컨테이너가 막음)
 */
export const buildStudyListCardHref = (item: RecentStudy): string => {
  const { byFeature } = commonRepo.permission

  switch (item.type) {
    case 'WORKSHEET':
    case 'HOMEWORK': {
      // studentWorksheetId 가 없으면 id 로 fallback (레거시 호환)
      const id = item.studentWorksheetId ?? item.id
      if (id == null) return '#'

      // 학습지/숙제 — CUSTOM_WORKSHEET_WRITE 단일 키로 판별
      const writeKey: StudentFeatureKey = 'CUSTOM_WORKSHEET_WRITE'
      const isParent = !byFeature.isAllowed(writeKey)

      if (isParent) {
        return generatePath(routeName.parent.studentWorksheetScoringById, {
          studentWorksheetId: String(id),
        })
      }
      return generatePath(routeName.student.studentWorksheetById, {
        studentWorksheetId: String(id),
      })
    }

    case 'WORKBOOK': {
      // 필수 식별자 부재 시 네비 불가
      if (item.studentWorkbookId == null || item.studentWorkbookRevisionId == null) {
        return '#'
      }

      // CUSTOM_WORKBOOK_WRITE 차단 = 학부모
      const isParent = !byFeature.isAllowed('CUSTOM_WORKBOOK_WRITE')

      // viewer/detail 분기는 resolveWorkbookViewRoute에서 SIGNATURE_WORKBOOK_PDF를 확인해서 먼저 판별
      // RecentStudy 응답엔 isSignatureWorkbook(=PDF 뷰어 사용 여부) 만 있어서 아카데미그룹교재도 커스텀시그니처교재 처럼 판별
      // 아카데미 그룹 교재를 사용하는 학원은 SIGNATURE_WORKBOOK_PDF가 안내려와서 영향 없음
      const workbookType: WorkbookDomain.Type | null = item.isSignatureWorkbook
        ? WorkbookDomain.TYPE.커스텀시그니처교재
        : null

      const view = workbookType
        ? resolveWorkbookViewRoute(workbookType, commonRepo.permission)
        : 'detail'

      const path =
        view === 'viewer'
          ? isParent
            ? routeName.parent.studentWorkbookViewer
            : routeName.student.studentWorkbookViewer
          : isParent
            ? routeName.parent.studentWorkbookDetail
            : routeName.student.studentWorkbookDetail

      return generatePath(path, {
        [studentWorkbookDetailPathParams.studentWorkbookId]: String(item.studentWorkbookId),
        [studentWorkbookDetailPathParams.revisionId]: String(item.studentWorkbookRevisionId),
        [studentWorkbookDetailPathParams.pageNumber]: String(
          item.studentWorkbookProgressPageNumber ?? 1,
        ),
      })
    }

    case 'EXAM': {
      if (item.studentExamId == null) return '#'

      // handleStudyCardClick 과 동일한 EXAM 차단 조건을 href 단계에서도 적용한다.
      if (
        isAcademyGroupAccessBlocked(item) ||
        !item.getIsAccessible() ||
        isExamResultBlocked(item)
      ) {
        return '#'
      }

      // EXAM_WORKSHEET_WRITE 차단 = 학부모
      const isParent = !byFeature.isAllowed('EXAM_WORKSHEET_WRITE')

      // 학부모는 학습지/숙제와 동일하게 항상 전체 뷰. 학생만 COMPLETE 시 마지막 문제로 점프.
      const isComplete = item.status === 'COMPLETE'
      const useMultiView = isParent || !isComplete
      const searchParams = createSearchParams({
        title: item.title ?? '',
        size: useMultiView ? 'all' : 'one',
        problemIndex: !useMultiView && item.problemsCount ? String(item.problemsCount - 1) : '0',
        examType: item.examType ?? 'EXAM',
      })
      const examRoute = isParent
        ? routeName.parent.studentExamScoringById
        : routeName.student.studentExamScoringById
      return `${generatePath(examRoute, {
        studentExamId: String(item.studentExamId),
      })}?${searchParams.toString()}`
    }

    case 'CAS': {
      if (item.assignedCasId == null) return '#'
      return generatePath(routeName.student.casByAssignedId, {
        assignedId: String(item.assignedCasId),
      })
    }

    default:
      return '#'
  }
}

/**
 * 시험 결과 확인이 막혀있는지 여부.
 * - EXAM 이 아니면 결과 비공개 개념이 없어 false
 * - COMPLETE/EXPIRED 이지만 시험 스케줄이 아직 RESOLVED 전이면 결과 조회 불가
 */
export const isExamResultBlocked = (item: RecentStudy): boolean => {
  if (item.type !== 'EXAM') return false
  const isDone = item.status === 'COMPLETE' || item.status === 'EXPIRED'
  return isDone && item.examScheduleProgress !== 'RESOLVED'
}

/**
 * 학원그룹(와이즈만 전국 수학학력평가) 시험 접근 차단 여부.
 * - 와이즈만은 매 시험의 문제가 동일 → IN_PROGRESS 외 상태에서는 노출/진입 차단
 * - examType !== 'ACADEMY_GROUP' 이면 false
 */
export const isAcademyGroupAccessBlocked = (item: RecentStudy): boolean => {
  if (item.examType !== 'ACADEMY_GROUP') return false
  return (
    item.examScheduleProgress !== 'IN_PROGRESS' ||
    item.status === 'COMPLETE' ||
    item.status === 'EXPIRED'
  )
}

/**
 * 학습 카드 클릭 공통 처리.
 * SOLO 미구독 학년 / 비공개 / 미응시 / 결과비공개 / ACADEMY_GROUP 차단 순으로 가드한 뒤,
 * 정상 이동인 경우에만 mixpanel 트래킹.
 */
export const handleStudyCardClick = (
  item: RecentStudy,
  e: MouseEvent<HTMLAnchorElement>,
  activeTabLabel: RecentStudyTabType,
): void => {
  // SOLO 미구독 학년 차단 — 이동 막고 토스트 노출
  const normalizedGrade = item.grade == null ? null : Number(item.grade)
  if (
    !commonRepo.permission.bySubscription.isSubscribed({
      schoolType: item.school,
      grade: normalizedGrade,
    })
  ) {
    e.preventDefault()
    toastService.info('구독 중인 학년이 아니에요.')
    return
  }

  if (item.isPrivate) {
    e.preventDefault()
    const isCas = item.type === 'CAS'
    const label = isCas ? tJosa('반응형평가') : '학습지'
    modalService.openModal(
      <Modal.Alert confirm={{ onClick: () => modalService.closeModal() }}>
        선생님이 비공개 한 {label}입니다.
        <br />
        공개하기 전까지는 볼 수 없어요!
      </Modal.Alert>,
      { modalName: isCas ? '비공개 자동시험 안내 모달' : '비공개 학습지 안내 모달' },
    )
    return
  }

  if (item.type === 'EXAM') {
    if (isAcademyGroupAccessBlocked(item)) {
      e.preventDefault()
      openAcademyGroupAccessBlockedModal()
      return
    }

    if (!item.getIsAccessible()) {
      e.preventDefault()
      modalService.openModal(
        <Modal.Alert confirm={{ onClick: () => modalService.closeModal() }}>
          미응시한 시험입니다.
        </Modal.Alert>,
        { modalName: '미응시 시험 접근 제한 모달' },
      )
      return
    }

    if (isExamResultBlocked(item)) {
      e.preventDefault()
      modalService.openModal(
        <Modal.Alert confirm={{ onClick: () => modalService.closeModal() }}>
          시험종료 후 확인할 수 있습니다.
        </Modal.Alert>,
        { modalName: '시험 결과 확인 모달' },
      )
      return
    }
  }

  mixpanelClient.track('학습홈_리스트_학습지_Click', {
    탭_이름: activeTabLabel,
    학습지타입: recentStudyTypeText[item.type],
    학습지상태:
      item.type === 'EXAM' || item.type === 'CAS'
        ? examLearningContentStatusText[item.status]
        : learningContentStatusText[item.status],
    학습지명: item.title,
  })
}
